import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';

import MentionList from './MentionList';

const MAX_ITEMS_DISPLAY = 10;

export const getSuggestionConfig = (suggestionOptions) => ({
  allowSpaces: true,
  items: ({ query }) => {
    const q = query.toLowerCase();

    // First priority: label matches
    const labelMatches = suggestionOptions.filter(({ label }) => label.toLowerCase().includes(q));

    if (labelMatches.length >= MAX_ITEMS_DISPLAY) {
      return labelMatches.slice(0, MAX_ITEMS_DISPLAY);
    }

    // Second priority: combined groupPath+label matches (excluding already matched)
    const labelMatchIds = new Set(labelMatches.map((item) => item.id));
    const combinedMatches = suggestionOptions.filter(({ id, label, groupPath }) => {
      if (labelMatchIds.has(id)) return false;
      const searchable = [...(groupPath || []), label].join(' ').toLowerCase();
      return searchable.includes(q);
    });

    return [...labelMatches, ...combinedMatches].slice(0, MAX_ITEMS_DISPLAY);
  },

  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },

      onUpdate(props) {
        component.updateProps(props);

        if (!props.clientRect) {
          return;
        }
        popup[0].setProps({
          getReferenceClientRect: props.clientRect,
        });
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup[0].hide();

          return true;
        }

        return component.ref?.onKeyDown(props);
      },

      onExit() {
        popup[0].destroy();
        component.destroy();
      },
    };
  },
});

/** Sort options recursively: directories (items with sub_items) first, then
 * alphabetically within each group. Subtrees are sorted by the same rule, so
 * `Claim › Incident Details` and the leaves inside it end up alphabetised
 * consistently with the top level instead of inheriting the source order
 * (NGTPA-23960 follow-up). The recursive step replaces each directory's
 * `sub_items` in-place so the return type stays the same shape as the input
 * (preserving inference for TS callers). */
export const sortSuggestionOptions = (options) => {
  const sorted = [...options].sort((a, b) => {
    const aHasChildren = a.sub_items && a.sub_items.length > 0;
    const bHasChildren = b.sub_items && b.sub_items.length > 0;

    // Directories first
    if (aHasChildren && !bHasChildren) return -1;
    if (!aHasChildren && bHasChildren) return 1;

    // Alphabetically within same type
    return a.label.localeCompare(b.label);
  });
  for (let i = 0; i < sorted.length; i++) {
    const item = sorted[i];
    if (item.sub_items && item.sub_items.length > 0) {
      sorted[i] = { ...item, sub_items: sortSuggestionOptions(item.sub_items) };
    }
  }
  return sorted;
};

export const flattenOptions = (options, parentPath = []) => {
  const flatList = [];

  options.forEach((item) => {
    if (item.sub_items) {
      const currentPath = [...parentPath, item.label];
      flatList.push(...flattenOptions(item.sub_items, currentPath));
    } else {
      flatList.push({
        label: item.label,
        id: item.id,
        groupPath: parentPath,
        ...(item.tags ? { tags: item.tags } : {}),
      });
    }
  });
  return flatList;
};

/**
 * Build hierarchical options structure from flat tokens with token_group.
 * Tokens with token_group are grouped into nested menus.
 * Tokens without token_group remain at root level.
 *
 * @param {Array} tokens - Flat array of tokens from API
 * @returns {Array} Hierarchical options structure for menus
 *
 * Example input:
 * [
 *   { token_key: 'CLAIM_NUMBER', display_name: 'Claim Number' },
 *   { token_key: 'API_CLAIM_LOB', display_name: 'LOB', token_group: ['API Claim'] },
 *   { token_key: 'API_CLAIM_INCIDENT_DATE', display_name: 'Date of Loss', token_group: ['API Claim', 'Incident Details'] }
 * ]
 *
 * Example output:
 * [
 *   { id: 'CLAIM_NUMBER', label: 'Claim Number' },
 *   { id: 'group_API Claim', label: 'API Claim', sub_items: [
 *     { id: 'API_CLAIM_LOB', label: 'LOB' },
 *     { id: 'group_API Claim_Incident Details', label: 'Incident Details', sub_items: [
 *       { id: 'API_CLAIM_INCIDENT_DATE', label: 'Date of Loss' }
 *     ]}
 *   ]}
 * ]
 */
export const buildHierarchicalOptions = (tokens) => {
  const rootItems = [];
  const groupMap = new Map(); // Map of group path string to group object

  tokens.forEach((token) => {
    const { token_key, display_name, token_group, tags, url_safe } = token;
    const leaf = {
      id: token_key,
      label: display_name,
      ...(tags ? { tags } : {}),
      ...(url_safe === undefined ? {} : { url_safe }),
    };

    if (!token_group || token_group.length === 0) {
      // No grouping - add to root
      rootItems.push(leaf);
      return;
    }

    // Build nested group structure
    let currentLevel = rootItems;
    let pathSoFar = '';

    token_group.forEach((groupName) => {
      pathSoFar = pathSoFar ? `${pathSoFar}_${groupName}` : groupName;
      const groupId = `group_${pathSoFar}`;

      let group = groupMap.get(pathSoFar);

      if (!group) {
        // Create new group
        group = {
          id: groupId,
          label: groupName,
          sub_items: [],
        };
        groupMap.set(pathSoFar, group);
        currentLevel.push(group);
      }

      // Move to next level
      currentLevel = group.sub_items;
    });

    // Add the actual token at the deepest level
    currentLevel.push(leaf);
  });

  return rootItems;
};
